///////////////////////////////////////////////////////////////////////////////////////////////////
/// @file   useStore.ts
/// @brief  Zustand store for the AWS License Admin Dashboard.
///
///         Simplified store with three slices: auth, customer, entitlement.
///
/// @copyright Wheatstone Corporation 2025, All rights reserved.
///////////////////////////////////////////////////////////////////////////////////////////////////

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { apiClient } from '../api/client';
import type { AuthResponse } from '../api/client';
import type {
  CustomerState,
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
} from './customer-slice';
import { initialCustomerState } from './customer-slice';
import type {
  EntitlementState,
  Entitlement,
  CreateEntitlementRequest,
  UpdateEntitlementRequest,
  ProvisionNanoBundleRequest,
  NanoBundleResult,
  CheckoutRequest,
  AuditLogFilters,
} from './entitlement-slice';
import { initialEntitlementState } from './entitlement-slice';
import type { ServerState } from './server-slice';
import { initialServerState } from './server-slice';

/// Monotonic id of the latest audit-log request (see fetchAuditLog).
let auditLogGeneration = 0;

const isAuthError = (error: unknown): boolean => {
  return error instanceof Error && error.message === 'Unauthorized';
};

interface AuthState {
  user: AuthResponse['user'] | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface UIState {
  sidebarCollapsed: boolean;
}

interface LicenseAdminStore {
  // Auth
  auth: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;

  // Customer
  customer: CustomerState;
  fetchCustomers: () => Promise<void>;
  createCustomer: (request: CreateCustomerRequest) => Promise<Customer>;
  updateCustomer: (id: number, request: UpdateCustomerRequest) => Promise<Customer>;
  deleteCustomer: (id: number) => Promise<void>;
  selectCustomer: (customer: Customer | null) => void;

  // Server
  server: ServerState;
  fetchServers: (customerId?: number) => Promise<void>;

  // Entitlement
  entitlement: EntitlementState;
  fetchEntitlements: (customerId?: number) => Promise<void>;
  createEntitlement: (request: CreateEntitlementRequest) => Promise<Entitlement>;
  provisionNanoBundle: (request: ProvisionNanoBundleRequest) => Promise<NanoBundleResult>;
  updateEntitlement: (id: number, request: UpdateEntitlementRequest) => Promise<Entitlement>;
  deleteEntitlement: (id: number) => Promise<void>;
  selectEntitlement: (entitlement: Entitlement | null) => void;
  fetchProducts: () => Promise<void>;
  fetchEntitlementAgents: () => Promise<void>;
  registerAgent: (req: { dev_uuid: string; agent_name: string }) => Promise<{ agent_id: string; status: string }>;
  checkoutLicense: (req: CheckoutRequest, ip?: string) => Promise<unknown>;
  releaseLicense: (leaseIds: number[]) => Promise<{ released: number }>;
  releaseAllLicenses: (agentId: number) => Promise<{ released: number }>;
  fetchEntitlementLeases: () => Promise<void>;
  fetchEntitlementAlarms: () => Promise<void>;
  fetchAuditLog: (filters?: AuditLogFilters) => Promise<void>;
  fetchDashboardSummary: () => Promise<void>;
  triggerCleanup: () => Promise<void>;
  clearAlarm: (alarmId: number) => Promise<void>;

  // UI
  ui: UIState;
  toggleSidebar: () => void;
}

const initialAuthState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const initialUIState: UIState = {
  sidebarCollapsed: false,
};

export const useStore = create<LicenseAdminStore>()(
  devtools(
    persist(
      (set, get) => ({
        auth: initialAuthState,
        customer: initialCustomerState,
        server: initialServerState,
        entitlement: initialEntitlementState,
        ui: initialUIState,

        // ── Auth ────────────────────────────────────────────────────────

        login: async (username: string, password: string) => {
          set((state) => ({
            auth: { ...state.auth, isLoading: true, error: null },
          }));

          try {
            const response = await apiClient.login({ username, password });
            set({
              auth: {
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              },
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed';
            set((state) => ({
              auth: { ...state.auth, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        logout: async () => {
          try {
            await apiClient.logout();
          } finally {
            set({ auth: initialAuthState });
          }
        },

        // ── Customers ──────────────────────────────────────────────────

        fetchCustomers: async () => {
          set((state) => ({
            customer: { ...state.customer, isLoading: true, error: null },
          }));

          try {
            const response = await apiClient.getCustomers();
            set((state) => ({
              customer: {
                ...state.customer,
                customers: response.customers,
                isLoading: false,
              },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
              return;
            }
            const message = error instanceof Error ? error.message : 'Failed to fetch customers';
            set((state) => ({
              customer: { ...state.customer, isLoading: false, error: message },
            }));
          }
        },

        createCustomer: async (request: CreateCustomerRequest) => {
          set((state) => ({
            customer: { ...state.customer, isLoading: true, error: null },
          }));

          try {
            const customer = await apiClient.createCustomer(request);
            set((state) => ({
              customer: {
                ...state.customer,
                customers: [...state.customer.customers, customer],
                isLoading: false,
              },
            }));
            get().fetchCustomers();
            return customer;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to create customer';
            set((state) => ({
              customer: { ...state.customer, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        updateCustomer: async (id: number, request: UpdateCustomerRequest) => {
          set((state) => ({
            customer: { ...state.customer, isLoading: true, error: null },
          }));

          try {
            const customer = await apiClient.updateCustomer(id, request);
            set((state) => ({
              customer: {
                ...state.customer,
                customers: state.customer.customers.map((c) =>
                  c.id === id ? customer : c
                ),
                isLoading: false,
              },
            }));
            get().fetchCustomers();
            return customer;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to update customer';
            set((state) => ({
              customer: { ...state.customer, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        deleteCustomer: async (id: number) => {
          try {
            await apiClient.deleteCustomer(id);
            set((state) => ({
              customer: {
                ...state.customer,
                customers: state.customer.customers.filter((c) => c.id !== id),
              },
            }));
            get().fetchCustomers();
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to delete customer';
            set((state) => ({
              customer: { ...state.customer, error: message },
            }));
            throw error;
          }
        },

        selectCustomer: (customer: Customer | null) => {
          set((state) => ({
            customer: { ...state.customer, selectedCustomer: customer },
          }));
        },

        // ── Servers ─────────────────────────────────────────────────────

        fetchServers: async (customerId?: number) => {
          set((state) => ({
            server: { ...state.server, isLoading: true, error: null },
          }));

          try {
            const response = await apiClient.getServers(customerId);
            set((state) => ({
              server: {
                ...state.server,
                servers: response.servers,
                isLoading: false,
              },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
              return;
            }
            const message = error instanceof Error ? error.message : 'Failed to fetch servers';
            set((state) => ({
              server: { ...state.server, isLoading: false, error: message },
            }));
          }
        },

        // ── Entitlements ───────────────────────────────────────────────

        fetchEntitlements: async (customerId?: number) => {
          set((state) => ({
            entitlement: { ...state.entitlement, isLoading: true, error: null },
          }));

          try {
            const response = await apiClient.getEntitlements(customerId);
            set((state) => ({
              entitlement: {
                ...state.entitlement,
                entitlements: response.entitlements,
                isLoading: false,
              },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
              return;
            }
            const message = error instanceof Error ? error.message : 'Failed to fetch entitlements';
            set((state) => ({
              entitlement: { ...state.entitlement, isLoading: false, error: message },
            }));
          }
        },

        createEntitlement: async (request: CreateEntitlementRequest) => {
          set((state) => ({
            entitlement: { ...state.entitlement, isLoading: true, error: null },
          }));

          try {
            const entitlement = await apiClient.createEntitlement(request);
            // Optimistic: add the new entitlement to local state immediately
            set((state) => ({
              entitlement: {
                ...state.entitlement,
                entitlements: [...state.entitlement.entitlements, entitlement],
                isLoading: false,
              },
            }));
            // Background refresh for accurate used_seats counts
            get().fetchEntitlements();
            return entitlement;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to create entitlement';
            set((state) => ({
              entitlement: { ...state.entitlement, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        provisionNanoBundle: async (request: ProvisionNanoBundleRequest) => {
          set((state) => ({
            entitlement: { ...state.entitlement, isLoading: true, error: null },
          }));
          try {
            const result = await apiClient.provisionNanoBundle(request);
            // Refresh to pull both newly-created entitlement rows with joined names.
            await get().fetchEntitlements();
            set((state) => ({
              entitlement: { ...state.entitlement, isLoading: false },
            }));
            return result;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to provision nano bundle';
            set((state) => ({
              entitlement: { ...state.entitlement, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        updateEntitlement: async (id: number, request: UpdateEntitlementRequest) => {
          set((state) => ({
            entitlement: { ...state.entitlement, isLoading: true, error: null },
          }));

          try {
            const entitlement = await apiClient.updateEntitlement(id, request);
            // Optimistic: replace the updated entitlement in local state
            set((state) => ({
              entitlement: {
                ...state.entitlement,
                entitlements: state.entitlement.entitlements.map((e) =>
                  e.id === id ? entitlement : e
                ),
                isLoading: false,
              },
            }));
            get().fetchEntitlements();
            return entitlement;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to update entitlement';
            set((state) => ({
              entitlement: { ...state.entitlement, isLoading: false, error: message },
            }));
            throw error;
          }
        },

        deleteEntitlement: async (id: number) => {
          try {
            await apiClient.deleteEntitlement(id);
            // Optimistic: remove from local state immediately
            set((state) => ({
              entitlement: {
                ...state.entitlement,
                entitlements: state.entitlement.entitlements.filter((e) => e.id !== id),
                selectedEntitlement:
                  state.entitlement.selectedEntitlement?.id === id
                    ? null
                    : state.entitlement.selectedEntitlement,
              },
            }));
            get().fetchEntitlements();
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            const message = error instanceof Error ? error.message : 'Failed to delete entitlement';
            set((state) => ({
              entitlement: { ...state.entitlement, error: message },
            }));
            throw error;
          }
        },

        selectEntitlement: (entitlement: Entitlement | null) => {
          set((state) => ({
            entitlement: { ...state.entitlement, selectedEntitlement: entitlement },
          }));
        },

        fetchProducts: async () => {
          try {
            const response = await apiClient.getProducts();
            set((state) => ({
              entitlement: { ...state.entitlement, products: response.products },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        fetchEntitlementAgents: async () => {
          try {
            const response = await apiClient.getEntitlementAgents();
            set((state) => ({
              entitlement: { ...state.entitlement, agents: response.agents },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        registerAgent: async (req: { dev_uuid: string; agent_name: string }) => {
          try {
            const result = await apiClient.registerAgent(req);
            await get().fetchEntitlementAgents();
            return result;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        checkoutLicense: async (req: CheckoutRequest, ip?: string) => {
          try {
            const result = await apiClient.checkoutLicense(req, ip);
            await get().fetchEntitlementLeases();
            return result;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        releaseLicense: async (leaseIds: number[]) => {
          try {
            const result = await apiClient.releaseLicenses(leaseIds);
            await get().fetchEntitlementLeases();
            return result;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        releaseAllLicenses: async (agentId: number) => {
          try {
            const result = await apiClient.releaseAllLicenses(agentId);
            await get().fetchEntitlementLeases();
            return result;
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        fetchEntitlementLeases: async () => {
          try {
            const response = await apiClient.getAdminLeases();
            set((state) => ({
              entitlement: { ...state.entitlement, leases: response.leases },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        fetchEntitlementAlarms: async () => {
          try {
            const response = await apiClient.getDashboardAlarms();
            set((state) => ({
              entitlement: { ...state.entitlement, alarms: response.alarms },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        fetchAuditLog: async (filters?: AuditLogFilters) => {
          // Only the newest request may land: a poll started just before a
          // filter change must not overwrite the filtered result.
          const generation = ++auditLogGeneration;
          try {
            const response = await apiClient.getAuditLog(filters);
            if (generation !== auditLogGeneration) return;
            set((state) => ({
              entitlement: { ...state.entitlement, auditLogs: response.logs },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        fetchDashboardSummary: async () => {
          try {
            const summary = await apiClient.getDashboardSummary();
            set((state) => ({
              entitlement: { ...state.entitlement, summary },
            }));
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
          }
        },

        triggerCleanup: async () => {
          try {
            await apiClient.triggerCleanup();
            await get().fetchEntitlementAlarms();
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        clearAlarm: async (alarmId: number) => {
          try {
            await apiClient.clearDashboardAlarm(alarmId);
            await Promise.all([get().fetchEntitlementAlarms(), get().fetchDashboardSummary()]);
          } catch (error) {
            if (isAuthError(error)) {
              set({ auth: initialAuthState });
            }
            throw error;
          }
        },

        // ── UI ─────────────────────────────────────────────────────────

        toggleSidebar: () => {
          set((state) => ({
            ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed },
          }));
        },
      }),
      {
        name: 'license-admin-storage',
        partialize: (state) => ({
          auth: { user: state.auth.user, isAuthenticated: state.auth.isAuthenticated },
          ui: { sidebarCollapsed: state.ui.sidebarCollapsed },
        }),
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<LicenseAdminStore>;
          return {
            ...currentState,
            auth: { ...currentState.auth, ...persisted.auth },
            ui: { ...currentState.ui, ...persisted.ui },
          };
        },
      }
    ),
    { name: 'LicenseAdminStore' }
  )
);

// Selector hooks
export const useAuth = () => useStore((state) => state.auth);
export const useCustomers = () => useStore((state) => state.customer);
export const useServers = () => useStore((state) => state.server);
export const useEntitlements = () => useStore((state) => state.entitlement);

export default useStore;
